import { Head } from "@inertiajs/react";
import { Container, Box, Grid, Stack, Group } from "@mantine/core";

interface Palette {
  id: string;
  name: string;
  tagline: string;
  vibe: string;
  base: string;
  surface: string;
  textPrimary: string;
  textDimmed: string;
  signature: string;
  signatureLabel: string;
  tech: string;
  dance: string;
  media: string;
  think: string;
}

const palettes: Palette[] = [
  {
    id: "diaspora",
    name: "Diaspora Modern",
    tagline: "Warm, culturally grounded, editorial",
    vibe: "cozy magazine · warm noir · signature terracotta",
    base: "#0F0B08",
    surface: "#1A1410",
    textPrimary: "#F5EEE0",
    textDimmed: "rgba(245,238,224,0.72)",
    signature: "#D4552B",
    signatureLabel: "Terracotta",
    tech: "#7BA4A5",
    dance: "#B8843D",
    media: "#E85D3C",
    think: "#4A6B4E",
  },
  {
    id: "neon-neo",
    name: "Neon Neo",
    tagline: "Dev-agency modern, single electric accent",
    vibe: "Vercel · Linear · Cursor — restraint + one bright pop",
    base: "#0A0A0A",
    surface: "#141414",
    textPrimary: "#F5F5F5",
    textDimmed: "rgba(245,245,245,0.62)",
    signature: "#B4F55B",
    signatureLabel: "Electric Chartreuse",
    tech: "#B4F55B",
    dance: "#E5E5E5",
    media: "#E5E5E5",
    think: "#E5E5E5",
  },
  {
    id: "sunset",
    name: "Sunset Cinematic",
    tagline: "Expressive, warm, Austin at golden hour",
    vibe: "film poster · music video · Austin sunset",
    base: "#1A0B1F",
    surface: "#24102C",
    textPrimary: "#F0E4D3",
    textDimmed: "rgba(240,228,211,0.72)",
    signature: "#E63A6E",
    signatureLabel: "Magenta",
    tech: "#F4A536",
    dance: "#E63A6E",
    media: "#FF6B4A",
    think: "#7B4B94",
  },
  {
    id: "afro",
    name: "Afrofuturism",
    tagline: "Culturally powerful, futuristic, distinctive",
    vibe: "Kendrick · Solange · Janelle Monáe visual worlds",
    base: "#1A0E24",
    surface: "#24152F",
    textPrimary: "#F5EEE0",
    textDimmed: "rgba(245,238,224,0.72)",
    signature: "#5E3C99",
    signatureLabel: "Royal Purple",
    tech: "#39FF14",
    dance: "#D4AF37",
    media: "#FF6B9D",
    think: "#8B5CF6",
  },
];

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <Stack gap={4} align="stretch" style={{ flex: 1, minWidth: 0 }}>
      <Box
        style={{
          height: 56,
          background: color,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      />
      <Box style={{ fontSize: 10, opacity: 0.8, lineHeight: 1.3 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ fontFamily: "monospace", opacity: 0.7 }}>{color}</div>
      </Box>
    </Stack>
  );
}

function PaletteCard({ palette }: { palette: Palette }) {
  return (
    <Box
      style={{
        background: palette.base,
        color: palette.textPrimary,
        borderRadius: 16,
        padding: 28,
        border: "1px solid rgba(255,255,255,0.06)",
        boxShadow: "0 12px 48px rgba(0,0,0,0.35)",
        display: "flex",
        flexDirection: "column",
        gap: 20,
        height: "100%",
      }}
    >
      {/* Header */}
      <Stack gap={4}>
        <Box
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.18em",
            color: palette.signature,
            fontWeight: 700,
          }}
        >
          {palette.id}
        </Box>
        <Box style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>
          {palette.name}
        </Box>
        <Box style={{ fontSize: 13, color: palette.textDimmed, lineHeight: 1.4 }}>
          {palette.tagline}
        </Box>
        <Box
          style={{
            fontSize: 11,
            color: palette.textDimmed,
            fontStyle: "italic",
            marginTop: 4,
          }}
        >
          {palette.vibe}
        </Box>
      </Stack>

      {/* Signature block */}
      <Box
        style={{
          background: palette.surface,
          borderRadius: 12,
          padding: 16,
          border: `1px solid ${palette.signature}33`,
        }}
      >
        <Group justify="space-between" mb={10}>
          <Box style={{ fontSize: 10, color: palette.textDimmed, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Signature
          </Box>
          <Box style={{ fontSize: 10, color: palette.textDimmed, fontFamily: "monospace" }}>
            {palette.signature}
          </Box>
        </Group>
        <Box
          style={{
            height: 64,
            background: palette.signature,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            color: palette.base,
            fontSize: 15,
            letterSpacing: "0.05em",
          }}
        >
          {palette.signatureLabel}
        </Box>
      </Box>

      {/* Pillar swatches */}
      <Stack gap={8}>
        <Box style={{ fontSize: 10, color: palette.textDimmed, textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Pillar accents
        </Box>
        <Group gap={8} wrap="nowrap">
          <Swatch color={palette.tech} label="Tech" />
          <Swatch color={palette.dance} label="Dance" />
          <Swatch color={palette.media} label="Media" />
          <Swatch color={palette.think} label="Think" />
        </Group>
      </Stack>

      {/* Sample UI */}
      <Stack gap={12}>
        <Box style={{ fontSize: 10, color: palette.textDimmed, textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Sample UI
        </Box>

        {/* Buttons */}
        <Group gap={8}>
          <Box
            style={{
              background: palette.signature,
              color: palette.base,
              padding: "10px 18px",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            View My Work
          </Box>
          <Box
            style={{
              background: "transparent",
              color: palette.textPrimary,
              padding: "10px 18px",
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 13,
              border: `1px solid ${palette.signature}66`,
            }}
          >
            Get In Touch
          </Box>
        </Group>

        {/* Sample card */}
        <Box
          style={{
            background: palette.surface,
            border: `1px solid ${palette.signature}22`,
            borderRadius: 12,
            padding: 16,
          }}
        >
          <Group gap={10} mb={8}>
            <Box
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: `linear-gradient(135deg, ${palette.tech}33, ${palette.tech}11)`,
                border: `1px solid ${palette.tech}33`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: palette.tech,
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              {"<>"}
            </Box>
            <Stack gap={0}>
              <Box style={{ fontSize: 10, color: palette.tech, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Build
              </Box>
              <Box style={{ fontSize: 16, fontWeight: 700 }}>Tech</Box>
            </Stack>
          </Group>
          <Box
            style={{
              display: "inline-flex",
              padding: "3px 9px",
              borderRadius: 999,
              background: `${palette.tech}18`,
              border: `1px solid ${palette.tech}33`,
              color: palette.tech,
              fontSize: 10,
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            Rails · React · n8n · AI
          </Box>
          <Box style={{ fontSize: 12, color: palette.textDimmed, lineHeight: 1.5 }}>
            Automation systems, full-stack apps, and AI integrations for organizations.
          </Box>
        </Box>

        {/* Typography sample */}
        <Box>
          <Box style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" }}>
            Ogarocious
          </Box>
          <Box style={{ fontSize: 14, fontWeight: 500, color: palette.textPrimary, marginTop: 2 }}>
            Tech, dance, media, and the writing that connects them.
          </Box>
          <Box style={{ fontSize: 12, color: palette.textDimmed, marginTop: 6, lineHeight: 1.6 }}>
            I build operational systems for organizations, teach Urban Kiz across cities and continents, shoot the events that shape Austin's creative scene, and write about the ideas connecting all of it.
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

export default function ThemePreview() {
  return (
    <>
      <Head>
        <title>Theme Preview | Ogarocious</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <Box
        style={{
          minHeight: "100vh",
          background: "linear-gradient(180deg, #050505 0%, #0a0a0a 100%)",
          color: "#f5eee0",
          padding: "48px 0",
        }}
      >
        <Container size="xl">
          <Stack gap="lg" mb={40}>
            <Box style={{ fontSize: 12, opacity: 0.7, letterSpacing: "0.2em", textTransform: "uppercase" }}>
              Palette Studies
            </Box>
            <Box style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.05 }}>
              Four Directions
            </Box>
            <Box style={{ fontSize: 16, opacity: 0.7, maxWidth: 640, lineHeight: 1.6 }}>
              Each palette shown with base, signature, four pillar accents, and sample UI so you can judge them in context — not just as color chips.
            </Box>
          </Stack>

          <Grid gutter="lg">
            {palettes.map((p) => (
              <Grid.Col key={p.id} span={{ base: 12, md: 6 }}>
                <PaletteCard palette={p} />
              </Grid.Col>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}
